// src/components/patient/appointments/RescheduleDialog.jsx
//
// Slot-based rescheduling (Patient Appointments Task 3):
//
//   choose dates -> slots for the PINNED service (sequenced; stale responses
//   dropped) -> pick a time -> destination hold (countdown from the server's
//   expires_at) -> confirm -> the server moves the appointment atomically and
//   only then releases the old time.
//
// The hold token lives in a ref only. Closing the dialog while holding releases
// the hold. A failed reschedule leaves the original appointment untouched, so
// the page just refreshes.

import React, { useCallback, useEffect, useRef, useState } from "react";

import { apiErrorCode, readApiError } from "../../../lib/apiErrors";
import { addDaysIso, dateRangeProblem, formatLocalDate, formatTimeInZone, todayInZone } from "../../../lib/displayTimezone";
import useSequencedRequest from "../../../lib/useSequencedRequest";
import { appointmentWorkspaceApi } from "../../../services/patientAppointmentWorkspace";
import { Pill, TimezoneNotice, modeLabel, modeTone } from "../booking/bookingUi";

import Dialog from "./Dialog";

const ENDED_CODES = new Set(["HOLD_EXPIRED", "HOLD_RELEASED", "HOLD_NOT_FOUND"]);
const RANGE_DAYS = 14;

function secondsLeft(expiresAt, offsetMs) {
  if (!expiresAt) return 0;
  return Math.max(0, Math.floor((new Date(expiresAt).getTime() - (Date.now() + offsetMs)) / 1000));
}

export default function RescheduleDialog({ open, appointment, displayTimezone, onClose, onRescheduled }) {
  const today = todayInZone(displayTimezone);
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(addDaysIso(today, RANGE_DAYS - 1));
  const [selected, setSelected] = useState(null);
  const [phase, setPhase] = useState("choose"); // choose | holding | held | moving | expired
  const [hold, setHold] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState("");
  const [replaceNeeded, setReplaceNeeded] = useState(false);

  const slots = useSequencedRequest(null);
  const tokenRef = useRef(null);
  const holdIdRef = useRef(null);
  const offsetRef = useRef(0);
  const movingRef = useRef(false);
  const rangeProblem = dateRangeProblem(dateFrom, dateTo);
  const busy = phase === "holding" || phase === "moving";
  const runSlots = slots.run;
  const cancelSlots = slots.cancel;

  const loadSlots = useCallback(() => {
    if (!open || !appointment?.id || rangeProblem) return;
    runSlots(({ signal }) =>
      appointmentWorkspaceApi.rescheduleSlots(
        appointment.id,
        { date_from: dateFrom, date_to: dateTo, display_timezone: displayTimezone, page_size: 100 },
        { signal }
      )
    );
  }, [open, appointment?.id, dateFrom, dateTo, displayTimezone, rangeProblem, runSlots]);

  useEffect(() => {
    loadSlots();
    return () => cancelSlots();
  }, [loadSlots, cancelSlots]);

  const releaseHold = useCallback(() => {
    const token = tokenRef.current;
    const holdId = holdIdRef.current;
    tokenRef.current = null;
    holdIdRef.current = null;
    if (token && holdId) appointmentWorkspaceApi.releaseHold(holdId, token).catch(() => {});
  }, []);

  // Unmount while holding: give the time back.
  useEffect(() => () => releaseHold(), [releaseHold]);

  useEffect(() => {
    if (phase !== "held") return undefined;
    const id = window.setInterval(() => {
      const left = secondsLeft(hold?.expires_at, offsetRef.current);
      setCountdown(left);
      if (left <= 0 && !movingRef.current) {
        tokenRef.current = null;
        setPhase("expired");
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [phase, hold]);

  function reset() {
    setSelected(null);
    setHold(null);
    setPhase("choose");
    setError("");
    setReplaceNeeded(false);
  }

  function close() {
    if (busy) return;
    releaseHold();
    reset();
    onClose?.();
  }

  async function holdSlot(slot, replaceExisting = false) {
    if (busy) return;
    setSelected(slot);
    setPhase("holding");
    setError("");
    setReplaceNeeded(false);
    try {
      const data = await appointmentWorkspaceApi.createRescheduleHold(appointment.id, {
        slotInventoryId: slot.slot_inventory_id,
        consultationMode: slot.consultation_mode,
        practiceLocationId: slot.practice_location_id,
        displayTimezone,
        replaceExisting,
      });
      tokenRef.current = data.hold_token;
      holdIdRef.current = data.hold_id;
      offsetRef.current = data.server_now ? new Date(data.server_now).getTime() - Date.now() : 0;
      setHold({ hold_id: data.hold_id, expires_at: data.expires_at });
      setCountdown(secondsLeft(data.expires_at, offsetRef.current));
      setPhase("held");
    } catch (err) {
      setPhase("choose");
      if (apiErrorCode(err) === "HOLD_REPLACEMENT_REQUIRED") {
        setReplaceNeeded(true);
        setError("You are already holding another time.");
        return;
      }
      setSelected(null);
      setError(readApiError(err, "That time could not be held. Please choose another."));
      loadSlots();
    }
  }

  async function confirmMove() {
    if (movingRef.current || phase !== "held" || !tokenRef.current) return;
    movingRef.current = true;
    setPhase("moving");
    setError("");
    try {
      const result = await appointmentWorkspaceApi.reschedule(appointment.id, {
        holdId: hold.hold_id,
        holdToken: tokenRef.current,
        slotInventoryId: selected.slot_inventory_id,
      });
      tokenRef.current = null;
      holdIdRef.current = null;
      reset();
      onRescheduled?.(result);
    } catch (err) {
      if (ENDED_CODES.has(apiErrorCode(err))) {
        tokenRef.current = null;
        setPhase("expired");
      } else {
        setPhase("held");
      }
      setError(readApiError(err, "Your appointment could not be moved. It is unchanged."));
    } finally {
      movingRef.current = false;
    }
  }

  const items = slots.data?.items || [];
  const byDate = items.reduce((acc, slot) => {
    (acc[slot.local_date] = acc[slot.local_date] || []).push(slot);
    return acc;
  }, {});
  const mins = `${Math.floor(countdown / 60)}`.padStart(2, "0");
  const secs = `${countdown % 60}`.padStart(2, "0");

  return (
    <Dialog open={open} title="Reschedule appointment" labelId="reschedule-title" busy={busy} onClose={close} wide>
      <p className="text-sm text-slate-700">
        {appointment?.service_name} with {appointment?.doctor_name}. Your service, price and forms stay the same.
      </p>
      <TimezoneNotice timeZone={displayTimezone} className="mt-1" />

      {error ? <p role="alert" className="mt-3 rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{error}</p> : null}
      {replaceNeeded && selected ? (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-2xl border border-amber-300 bg-amber-50 p-3 text-sm">
          <span>Holding this time will release the other time you are holding.</span>
          <button type="button" onClick={() => holdSlot(selected, true)} className="rounded-2xl bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white">Hold this time instead</button>
        </div>
      ) : null}

      {phase === "choose" || phase === "holding" ? (
        <>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="reschedule-from" className="text-xs font-semibold">From</label>
              <input id="reschedule-from" type="date" value={dateFrom} min={today} onChange={(e) => setDateFrom(e.target.value)} disabled={busy}
                aria-invalid={rangeProblem ? "true" : undefined} aria-describedby={rangeProblem ? "reschedule-range-error" : undefined}
                className="mt-1 w-full rounded-2xl border border-slate-300 px-3 py-2 text-sm" />
            </div>
            <div>
              <label htmlFor="reschedule-to" className="text-xs font-semibold">To</label>
              <input id="reschedule-to" type="date" value={dateTo} min={dateFrom} onChange={(e) => setDateTo(e.target.value)} disabled={busy}
                aria-invalid={rangeProblem ? "true" : undefined} aria-describedby={rangeProblem ? "reschedule-range-error" : undefined}
                className="mt-1 w-full rounded-2xl border border-slate-300 px-3 py-2 text-sm" />
            </div>
          </div>
          {rangeProblem ? <p id="reschedule-range-error" role="alert" className="mt-1 text-xs font-semibold text-rose-700">{rangeProblem}</p> : null}

          <div className="mt-4" aria-live="polite" aria-busy={slots.loading || undefined} data-testid="reschedule-slots">
            {slots.loading && !slots.data ? <p className="text-sm text-slate-500">Loading available times…</p> : null}
            {slots.error ? (
              <div role="alert" className="flex items-center justify-between gap-2 rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                <span>{readApiError(slots.error, "Available times could not be loaded.")}</span>
                <button type="button" onClick={loadSlots} className="rounded-xl border border-rose-300 bg-white px-3 py-1 text-sm font-semibold">Retry</button>
              </div>
            ) : null}
            {slots.data && !items.length && !slots.error ? <p className="text-sm text-slate-600">No other times are available in these dates. Try different dates.</p> : null}
            {Object.entries(byDate).map(([day, daySlots]) => (
              <div key={day} className="mb-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{formatLocalDate(day)}</h3>
                <div className="mt-2 flex flex-wrap gap-2">
                  {daySlots.map((slot) => {
                    const isSelected = selected?.slot_inventory_id === slot.slot_inventory_id;
                    return (
                      <button
                        key={slot.slot_inventory_id}
                        type="button"
                        onClick={() => holdSlot(slot)}
                        disabled={busy}
                        aria-pressed={isSelected}
                        className={`rounded-2xl border px-3 py-2 text-sm font-semibold ${isSelected ? "border-sky-500 bg-sky-50" : "border-slate-300 bg-white hover:bg-slate-50"} disabled:opacity-60`}
                      >
                        {formatTimeInZone(slot.start_at, displayTimezone)}
                        <span className="ml-1 text-xs font-normal text-slate-500">{modeLabel(slot.consultation_mode)}{slot.location_name ? ` · ${slot.location_name}` : ""}</span>
                        {isSelected && phase === "holding" ? <span className="ml-1 text-xs">Holding…</span> : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}

      {(phase === "held" || phase === "moving" || phase === "expired") && selected ? (
        <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid="reschedule-hold">
          <p className="text-sm font-semibold">New time</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Pill tone="info">{formatLocalDate(selected.local_date)}</Pill>
            <Pill tone="neutral">{formatTimeInZone(selected.start_at, displayTimezone)} – {formatTimeInZone(selected.end_at, displayTimezone)}</Pill>
            <Pill tone={modeTone(selected.consultation_mode)}>{modeLabel(selected.consultation_mode)}</Pill>
          </div>
          <p className="mt-3 text-sm" aria-live="polite" data-testid="reschedule-countdown">
            {phase === "expired" ? "This hold has ended. Choose a time again." : `Held for ${mins}:${secs}`}
          </p>
          <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button type="button" onClick={() => { releaseHold(); reset(); loadSlots(); }} disabled={phase === "moving"} className="rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold disabled:opacity-50">
              Choose another time
            </button>
            {phase !== "expired" ? (
              <button type="button" onClick={confirmMove} disabled={phase !== "held" || countdown <= 0} aria-busy={phase === "moving" || undefined} className="rounded-2xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
                {phase === "moving" ? "Rescheduling…" : "Confirm new time"}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </Dialog>
  );
}
